"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.KnowledgeUIHelper = void 0;
/**
 * Static helper class for UI-related knowledge picker utilities
 */
class KnowledgeUIHelper {
    /**
     * Format file size in bytes to human readable format
     */
    static formatFileSize(bytes) {
        if (!bytes || bytes === 0)
            return 'N/A';
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    }
    /**
     * Format MIME type to human readable file type
     */
    static formatFileType(contentType, mimeType) {
        var _a;
        if (contentType === 'text') {
            return 'Text';
        }
        if (contentType === 'link') {
            return 'Link';
        }
        if (contentType === 'google-drive') {
            return 'GDrive';
        }
        if (!mimeType)
            return '-';
        const typeMap = {
            'application/pdf': 'PDF',
            'application/msword': 'DOC',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
            'text/plain': 'TXT',
            'text/markdown': 'MD',
            'text/html': 'HTML',
            'text/css': 'CSS',
            'text/javascript': 'JS',
            'application/javascript': 'JS',
            'application/typescript': 'TS',
            'application/json': 'JSON',
            'text/csv': 'CSV',
            'application/vnd.ms-excel': 'XLS',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX'
        };
        return typeMap[mimeType] || ((_a = mimeType.split('/')[1]) === null || _a === void 0 ? void 0 : _a.toUpperCase()) || 'Unknown';
    }
    /**
     * Check viewport position for dropdown positioning
     */
    static checkDropdownPosition(element, dropdownMinHeight = 320, spaceThreshold = 150) {
        const rect = element.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceAbove = rect.top;
        const spaceBelow = viewportHeight - rect.bottom;
        // Use bottom position if there's not enough space above
        const isBottomPosition = spaceAbove < dropdownMinHeight && spaceBelow > spaceThreshold;
        return { isBottomPosition, spaceAbove, spaceBelow };
    }
    /**
     * Apply dropdown positioning styles
     */
    static applyDropdownPositioning(element, isBottomPosition) {
        if (isBottomPosition) {
            element.classList.add('bottom-position');
        }
        else {
            element.classList.remove('bottom-position');
        }
    }
    /**
     * Debounce rapid clicks/actions
     */
    static isDebouncedAction(key, debounceTimeMs = 100) {
        const now = Date.now();
        const lastAction = this.debounceMap.get(key) || 0;
        if (now - lastAction < debounceTimeMs) {
            console.log(`Ignoring rapid action for key: ${key}`);
            return true; // Should be debounced (ignored)
        }
        this.debounceMap.set(key, now);
        return false; // Should not be debounced (proceed)
    }
    /**
     * Create keyboard event handler for ESC key
     */
    static createEscKeyHandler(onEscape) {
        return (event) => {
            if (event.key === 'Escape') {
                onEscape();
            }
        };
    }
    /**
     * Create resize/scroll handler with debouncing
     */
    static createPositionHandler(callback, debounceMs = 16) {
        let timeoutId = null;
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            timeoutId = setTimeout(callback, debounceMs);
        };
    }
    /**
     * Focus management utility
     */
    static manageFocus(element, shouldFocus) {
        if (!element)
            return;
        if (shouldFocus) {
            // Small delay to ensure element is rendered
            setTimeout(() => {
                element.focus();
            }, 10);
        }
        else {
            element.blur();
        }
    }
    /**
     * Create delayed action (useful for dropdown hide delays)
     */
    static createDelayedAction(callback, delayMs) {
        return setTimeout(callback, delayMs);
    }
    /**
     * Validate event detail for switch components
     */
    static isValidSwitchEvent(event, expectedProperty = 'checked') {
        return event.detail && event.detail.hasOwnProperty(expectedProperty);
    }
    /**
     * Clear all debounce entries (cleanup utility)
     */
    static clearAllDebounces() {
        this.debounceMap.clear();
    }
}
exports.KnowledgeUIHelper = KnowledgeUIHelper;
KnowledgeUIHelper.debounceMap = new Map();
